<script setup lang="ts">
import type { Category } from '~/types/blog'
import { normalizeFieldText } from '~/composables/useCustomFields'

const route = useRoute()
const slug = String(route.params.slug || '')
const { isStaleNavigation } = useNavigationStaleGuard()

const blogApi = useBlogApi()
const siteData = await useSiteData()
const siteBaseUrl = resolveSiteBaseUrl(siteData?.setting?.customFields)
const { data: categoriesData } = await useAsyncData(`category-tree:${slug}`, async (_nuxtApp, { signal }) => {
  try {
    return await blogApi.getCategoryTree({ signal })
  } catch (error) {
    if (isStaleNavigation() || isAbortLikeError(error)) {
      return [] as Category[]
    }
    throw error
  }
})
const categories = categoriesData.value || []

const findCategory = (list: Category[]): Category | null => {
  for (const item of list) {
    if (item.slug === slug) {
      return item
    }
    if (item.children?.length) {
      const hit = findCategory(item.children)
      if (hit) {
        return hit
      }
    }
  }
  return null
}

const category = findCategory(categories)
if (!category && !isStaleNavigation()) {
  throw createError({ status: 404, message: '分类不存在' })
}

const page = computed(() => Math.max(1, Number(route.query.page || 1)))
const categoryID = Number(category?.ID || 0)
const { data } = await useAsyncData(
  () => `category:${slug}:${page.value}`,
  () => {
    if (categoryID <= 0) {
      return Promise.resolve({ list: [], total: 0, page: page.value, pageSize: 10 })
    }
    return blogApi.getContentMetaList({ page: page.value, pageSize: 10, type: 'post', categoryId: categoryID })
  },
  { watch: [page] }
)

const siteName = normalizeFieldText(siteData?.setting?.customFields, 'siteName', 'Yuelai Blog')
const siteDescription = normalizeFieldText(
  siteData?.setting?.customFields,
  'siteDescription',
  normalizeFieldText(siteData?.setting?.customFields, 'seoDescription', '')
)
const description = category?.description || siteDescription

useSeoMeta({
  title: `${category?.name || '分类'} - ${siteName}`,
  description,
  ogTitle: `${category?.name || '分类'} - ${siteName}`,
  ogDescription: description
})

useHead({
  link: [{ rel: 'canonical', href: `${siteBaseUrl}${route.path}` }]
})
</script>

<template>
  <section>
    <div class="card header-card">
      <h1><i class="fa-solid fa-folder-open" aria-hidden="true" />分类：{{ category?.name }}</h1>
      <p>{{ category?.description || '该分类下的全部文章。' }}</p>
    </div>

    <template v-if="(data?.list || []).length">
      <BlogPostCard v-for="item in data?.list || []" :key="item.ID" :item="item" />
      <BlogPager :page="page" :page-size="data?.pageSize || 10" :total="data?.total || 0" />
    </template>

    <div v-else class="empty-card">该分类暂无文章</div>
  </section>
</template>

<style scoped>
.header-card {
  padding: 20px;
  margin-bottom: 14px;
}

h1 {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 30px;
}

p {
  margin-top: 8px;
  color: var(--text-soft);
}
</style>
